'use client'

import type { ReactNode } from 'react'
import React, { useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { ChevronDown, HardDrive } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import { Button, Skeleton } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { starOnLessonArray } from '@helper/utils/business-logic'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { RewardStar } from '../../../components'
import { useSharedTRPC } from '../../../context'

type LessonHistoryRow = RouterOutputs['metrics']['getLessonHistory']['data'][number]

/**
 * The mission a history row points at. `course`/`challenge` are supported by every app's
 * `/belajar/t/misi` route; `learnEnrollment` is web-only, so consumers may skip it (e.g. practice
 * rows in the school app) by returning nothing from the render prop.
 */
export type LessonModuleLinkTarget =
  | { type: 'course'; id: string }
  | { type: 'challenge'; id: string }
  | { type: 'learnEnrollment'; id: string }

/** Render prop so each app wraps the CTA in its own router `<Link>` to `/belajar/t/misi`. */
export type RenderToLessonModuleLink = (args: { target: LessonModuleLinkTarget; content: ReactNode }) => ReactNode

/** Prefer the course/challenge id (works in every app); fall back to the enrollment id (web-only). */
function linkTarget(row: LessonHistoryRow): LessonModuleLinkTarget {
  if (row.type === 'Tugas' && row.courseId) return { type: 'course', id: row.courseId }
  if (row.type === 'Tantangan' && row.challengeId) return { type: 'challenge', id: row.challengeId }
  return { type: 'learnEnrollment', id: row.enrollmentId }
}

const STATUS_LABEL: Record<LessonHistoryRow['status'], string> = {
  NOT_STARTED: 'Hanya lihat-lihat',
  IN_PROGRESS: 'Sedang dikerjakan',
  COMPLETED: 'Sukses selesai',
  FAILED: 'Gagal selesai',
}

/** score is a 0–100 grade; map to 0..3 stars via the shared graded-XP ladder (null grade → 0). */
function scoreToStars(score: number | null): 0 | 1 | 2 | 3 {
  if (score == null) return 0
  return starOnLessonArray({ type: 'graded_xp', gradedXp: score }).filter((s) => s.isFilled).length as 0 | 1 | 2 | 3
}

/** Course rows show the course title; challenge/practice rows keep the module/challenge title snapshot. */
function displayTitle(row: LessonHistoryRow): string {
  if (row.type === 'Tugas') return row.courseTitle ?? row.lessonModuleTitle
  return row.lessonModuleTitle
}

function displaySubtitle(row: LessonHistoryRow): string {
  if (row.type === 'Tugas') return row.teacherName ? `Tugas dari ${row.teacherName}` : 'Tugas'
  if (row.type === 'Mandiri') return 'Latihan'
  return 'Tantangan'
}

function formatDay(iso: string | null): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '-'
  return formatDate(d, { dateStyle: 'long' })
}

interface LessonHistoryProps {
  childId: string
  className?: string
  renderToLessonModuleLink?: RenderToLessonModuleLink
}

// Row grid: cover+title | progress | status | stars | dates | CTA. min-width keeps columns aligned;
// the list scrolls horizontally on narrow screens instead of squishing (mirrors reading-history).
const rowGridClassName =
  'srd:grid srd:min-w-[900px] srd:grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto_minmax(0,1.3fr)_auto] srd:items-center srd:gap-4 srd:px-4 srd:py-3'

function LessonHistoryItem({
  row,
  renderToLessonModuleLink,
}: {
  row: LessonHistoryRow
  renderToLessonModuleLink?: RenderToLessonModuleLink
}) {
  const stars = scoreToStars(row.score)

  return (
    <div className={rowGridClassName}>
      <div className='srd:flex srd:min-w-0 srd:items-center srd:gap-4'>
        {row.coverUrl ? (
          <img
            src={row.coverUrl}
            alt={displayTitle(row)}
            width={56}
            height={75}
            draggable={false}
            className='srd:aspect-3/4 srd:w-14 srd:shrink-0 srd:rounded-sm srd:object-cover'
          />
        ) : (
          <div className='srd:aspect-3/4 srd:w-14 srd:shrink-0 srd:rounded-sm srd:bg-grey-10' />
        )}
        <div className='srd:flex srd:min-w-0 srd:flex-col'>
          <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:line-clamp-2 srd:text-neutral-black')}>
            {displayTitle(row)}
          </p>
          <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:line-clamp-1 srd:text-neutral-black')}>
            {displaySubtitle(row)}
          </p>
        </div>
      </div>

      <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}>
        {row.completedLessons} dari {row.totalLessons} selesai
      </p>

      <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
        {STATUS_LABEL[row.status]}
      </p>

      <div className='srd:w-16 srd:shrink-0'>
        <RewardStar stars={stars} />
      </div>

      <div className='srd:flex srd:min-w-0 srd:flex-col'>
        <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
          {formatDay(row.startedAt ?? row.createdAt)}
        </p>
        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
          Diperbarui {formatDay(row.updatedAt)}
        </p>
      </div>

      <div className='srd:shrink-0'>
        {renderToLessonModuleLink?.({
          target: linkTarget(row),
          content: (
            <span
              className={cn(
                typographyVariants({ variant: 'label-md' }),
                'srd:inline-flex srd:items-center srd:justify-center srd:rounded-lg srd:border srd:border-blue-70 srd:px-4 srd:py-2 srd:text-navy-80 srd:transition-colors srd:hover:bg-blue-30',
              )}
            >
              Lihat
            </span>
          ),
        })}
      </div>
    </div>
  )
}

interface LessonHistoryListProps {
  groupedByDate: [string, LessonHistoryRow[]][]
  isPending: boolean
  isError: boolean
  isEmpty: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean | undefined
  pageSize: number
  refetch: () => void
  fetchNextPage: () => void
  renderToLessonModuleLink?: RenderToLessonModuleLink
  className?: string
}

const LessonHistoryList = React.memo(function LessonHistoryList({
  groupedByDate,
  isPending,
  isError,
  isEmpty,
  isFetchingNextPage,
  hasNextPage,
  pageSize,
  refetch,
  fetchNextPage,
  renderToLessonModuleLink,
  className,
}: LessonHistoryListProps) {
  if (isError) {
    return (
      <div className='srd:flex srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-2 srd:py-8'>
        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-main-red-100')}>
          Gagal memuat riwayat belajar
        </p>
        <Button size='sm' onClick={() => refetch()} variant='outline'>
          Coba lagi
        </Button>
      </div>
    )
  }

  if (isPending) {
    return (
      <div className={cn('srd:w-full srd:overflow-x-auto', className)}>
        <div className='srd:flex srd:flex-col srd:gap-1'>
          <Skeleton className='srd:h-9 srd:min-w-[900px] srd:rounded-lg' />
          <div className='srd:flex srd:flex-col srd:divide-y srd:divide-outline-variant/50'>
            {new Array(pageSize).fill(0).map((_, i) => (
              <div key={i} className={rowGridClassName}>
                <div className='srd:flex srd:items-center srd:gap-4'>
                  <Skeleton className='srd:aspect-3/4 srd:w-14 srd:rounded-sm' />
                  <div className='srd:flex srd:flex-col srd:gap-1'>
                    <Skeleton className='srd:h-4 srd:w-40' />
                    <Skeleton className='srd:h-3 srd:w-24' />
                  </div>
                </div>
                <Skeleton className='srd:h-4 srd:w-24' />
                <Skeleton className='srd:h-4 srd:w-24' />
                <Skeleton className='srd:h-6 srd:w-16' />
                <Skeleton className='srd:h-4 srd:w-28' />
                <Skeleton className='srd:h-9 srd:w-16' />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-2 srd:py-8 srd:text-grey-100'>
        <HardDrive />
        <p className={cn(typographyVariants({ variant: 'body-sm' }))}>Belum ada riwayat belajar</p>
      </div>
    )
  }

  return (
    <div className={cn('srd:w-full srd:overflow-x-auto', className)}>
      <div className='srd:flex srd:flex-col srd:gap-4'>
        {groupedByDate.map(([dateKey, groupItems]) => (
          <div key={dateKey} className='srd:flex srd:flex-col srd:gap-1'>
            <div className='srd:min-w-[900px] srd:rounded-lg srd:bg-grey-30 srd:px-4 srd:py-2'>
              <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:font-bold srd:text-neutral-black')}>
                {dateKey}
              </p>
            </div>
            <div className='srd:flex srd:flex-col srd:divide-y srd:divide-outline-variant/50'>
              {groupItems.map((item) => (
                <LessonHistoryItem
                  key={`${item.enrollmentId}-${item.date}`}
                  row={item}
                  renderToLessonModuleLink={renderToLessonModuleLink}
                />
              ))}
            </div>
          </div>
        ))}

        {hasNextPage && (
          <div className='srd:flex srd:justify-center srd:py-2'>
            <Button size='sm' onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
              {isFetchingNextPage ? 'Memuat...' : 'Muat lebih banyak'}
              <ChevronDown className='srd:size-4 srd:ml-2' />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
})

export function LessonHistory({ childId, className, renderToLessonModuleLink }: LessonHistoryProps) {
  const trpc = useSharedTRPC()

  const [pageSize] = useState(10)

  const input = useMemo(
    () =>
      ({
        userId: childId,
        pageSize,
      }) as const,
    [childId, pageSize],
  )

  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    trpc.metrics.getLessonHistory.infiniteQueryOptions(input, {
      getNextPageParam: (lastPage: { nextCursor?: number }) => lastPage.nextCursor,
      initialCursor: 1,
    }),
  )

  const allRows = useMemo(
    () => data?.pages.flatMap((p) => (p as { data: RouterOutputs['metrics']['getLessonHistory']['data'] }).data) ?? [],
    [data],
  )

  const groupedByDate = useMemo(() => {
    if (allRows.length === 0) return []
    const groups: Record<string, LessonHistoryRow[]> = {}
    for (const row of allRows) {
      if (!row.date) continue
      const dateObj = new Date(row.date)
      if (isNaN(dateObj.getTime())) continue
      const dateKey = formatDate(dateObj, { dateStyle: 'long' })
      groups[dateKey] ??= []
      groups[dateKey].push(row)
    }
    const sortedEntries = Object.entries(groups).sort(([, a], [, b]) => {
      const dateA = a[0]?.date ? new Date(a[0].date).getTime() : 0
      const dateB = b[0]?.date ? new Date(b[0].date).getTime() : 0
      return dateB - dateA
    })
    return sortedEntries
  }, [allRows])

  return (
    <LessonHistoryList
      groupedByDate={groupedByDate}
      isPending={isPending}
      isError={isError}
      isEmpty={allRows.length === 0}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      pageSize={pageSize}
      refetch={refetch}
      fetchNextPage={fetchNextPage}
      renderToLessonModuleLink={renderToLessonModuleLink}
      className={className}
    />
  )
}
